/**
 * How uploaded files are named in the request's folder. The names are what
 * an agent sees on the Dashboard and in Zoho, so these rules are kept exactly
 * as the pages have always applied them.
 */

/** a-z and 0-9 only: "Green card — Wei Chen" → "green-card-wei-chen". */
export function slugify(value) {
  return (
    (value || "document")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "document"
  );
}

/**
 * Each block's own file-name stem, from its label. slugify keeps only a-z
 * and 0-9, so labels that differ only in a Chinese name ("Green card — 王伟",
 * "Green card — 李梅", one per covered person) would otherwise share a name.
 * A clash gets -2, -3… in block order. Returns a new array.
 */
export function assignFileStems(blocks) {
  const used = new Set();
  return (blocks || []).map((block) => {
    const base = slugify(block.label);
    let stem = base;
    for (let n = 2; used.has(stem); n += 1) stem = `${base}-${n}`;
    used.add(stem);
    return { ...block, fileStem: stem };
  });
}

/** "<stem>__<side>__<original-name>.<ext>": which block, which side, which file. */
export function buildDecoratedName(fileStem, subLabel, originalName) {
  const subSlug = slugify(subLabel || "file");
  const extension = originalName.includes(".") ? originalName.slice(originalName.lastIndexOf(".")) : "";
  const baseName = originalName.replace(extension, "");
  return `${fileStem}__${subSlug}__${slugify(baseName)}${extension}`;
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function dataUrlToBlob(dataUrl) {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "application/octet-stream";
  const binary = atob(data || "");
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) buffer[i] = binary.charCodeAt(i);
  return new Blob([buffer], { type: mime });
}

/** The text file a typed answer or an agreement is uploaded as. */
export function textFile(content, fileName) {
  return new File([new Blob([content], { type: "text/plain" })], fileName, { type: "text/plain" });
}
